<!--
  新規ファイル(2026-09-09): 試着室 電波モニタ(pingログ)

  機器設定(アンテナの向き・出力の調整)時に使う計測用画面。
  IoT から届いた ping を【判定前のまま】表示する。
  RSSI閾値(-65)に届かない弱い反応や、m_fitting_room に未登録のアンテナも出る。
  そこが見えないとアンテナ調整の判断ができないため。

  データ元: prduser.t_fitting_room_ping_log
    確定処理Lambdaが閾値判定より前に1行ずつINSERTする。
    保持件数は m_setting.FITTING_PING_LOG_MAX_ROWS。超過分はスイープLambdaが削除する。
    0 にすると記録が止まり、残った行も次のスイープで消える(計測終了後はこれで止める)。
-->
<template>
  <!-- v-app / v-main は既存画面(FittingRoomHistoryPage 等)と同じ構造。
       v-main が無いと左サイドバー分のオフセットが効かず、
       表の左端(RSSI・アンテナ)がメニューの下に潜り、上部も見切れる。 -->
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>

      <!-- 記録が止まっているときの警告 -->
      <v-alert v-if="maxRows === 0" type="warning" density="compact" class="mb-3">
        {{ $t('fittingRoomPingLog.label.stopped') }}
      </v-alert>

      <!-- 操作パネル -->
      <v-card class="mb-3">
        <v-card-text>
          <v-row align="center" dense>
            <v-col cols="12" sm="2">
              <v-switch
                v-model="autoRefresh"
                :label="autoRefresh ? $t('fittingRoomPingLog.label.autoRefreshOn') : $t('fittingRoomPingLog.label.autoRefreshOff')"
                color="primary"
                density="compact"
                hide-details
                @update:modelValue="onToggleAutoRefresh" />
            </v-col>

            <v-col cols="6" sm="2">
              <v-select
                v-model="intervalSec"
                :items="intervalItems"
                item-title="label"
                item-value="value"
                :label="$t('fittingRoomPingLog.label.interval')"
                density="compact"
                hide-details
                :disabled="!autoRefresh"
                @update:modelValue="restartTimer" />
            </v-col>

            <v-col cols="6" sm="2">
              <v-text-field
                v-model="searchCondition.antennaNo"
                :label="$t('fittingRoomPingLog.label.antennaNo')"
                density="compact"
                hide-details
                @keyup.enter="search" />
            </v-col>

            <v-col cols="6" sm="3">
              <v-text-field
                v-model="searchCondition.epc"
                :label="$t('fittingRoomPingLog.label.epc')"
                density="compact"
                hide-details
                @keyup.enter="search" />
            </v-col>

            <v-col cols="6" sm="2">
              <v-checkbox
                v-model="searchCondition.judgedOnly"
                :label="$t('fittingRoomPingLog.label.judgedOnly')"
                density="compact"
                hide-details
                @update:modelValue="search" />
            </v-col>

            <v-col cols="12" sm="1" class="text-right">
              <v-btn color="primary" :loading="isSearching" @click="search">{{ $t('fittingRoomPingLog.label.refreshNow') }}</v-btn>
            </v-col>
          </v-row>

          <v-row dense class="mt-2">
            <v-col cols="12">
              <span class="text-caption">
                {{ $t('fittingRoomPingLog.label.status', { shown: dataList.length, total: totalRows, max: maxRows, time: lastUpdatedLabel }) }}
                <span v-if="errorMessage" class="text-error ml-2">{{ errorMessage }}</span>
              </span>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- 一覧(新しい順) -->
      <v-table density="compact" fixed-header height="60vh">
        <thead>
          <tr>
            <th class="text-right">{{ $t('fittingRoomPingLog.label.table.rssi') }}</th>
            <th>{{ $t('fittingRoomPingLog.label.table.antenna') }}</th>
            <th>{{ $t('fittingRoomPingLog.label.table.fittingRoom') }}</th>
            <th>{{ $t('fittingRoomPingLog.label.table.judged') }}</th>
            <th>{{ $t('fittingRoomPingLog.label.table.epc') }}</th>
            <th>{{ $t('fittingRoomPingLog.label.table.janCode') }}</th>
            <th>{{ $t('fittingRoomPingLog.label.table.machineNo') }}</th>
            <th>{{ $t('fittingRoomPingLog.label.table.pingDatetime') }}</th>
            <th>{{ $t('fittingRoomPingLog.label.table.receivedDatetime') }}</th>
            <th class="text-right">{{ $t('fittingRoomPingLog.label.table.gap') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in dataList" :key="row.id" :class="rowClass(row)">
            <td class="text-right font-weight-bold">{{ row.rssi }}</td>
            <td>{{ row.antennaNo }}</td>
            <td>
              <span v-if="row.fittingRoomNo">{{ row.fittingRoomNo }}</span>
              <span v-else class="text-error">{{ $t('fittingRoomPingLog.label.unmapped') }}</span>
            </td>
            <td>
              <v-chip v-if="row.judged" color="success" size="x-small">{{ $t('fittingRoomPingLog.label.judgedYes') }}</v-chip>
              <v-chip v-else color="grey" size="x-small">{{ $t('fittingRoomPingLog.label.judgedNo') }}</v-chip>
            </td>
            <td class="text-mono">{{ row.epc }}</td>
            <td>{{ row.janCode }}</td>
            <td>{{ row.readerId }}</td>
            <td>{{ row.pingLabel }}</td>
            <td>{{ row.recvLabel }}</td>
            <td class="text-right">{{ row.gapLabel }}</td>
          </tr>
          <tr v-if="dataList.length === 0">
            <td colspan="10" class="text-center text-disabled">{{ $t('fittingRoomPingLog.label.noData') }}</td>
          </tr>
        </tbody>
      </v-table>

        </v-container>
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import api from "@/apis/staff";

export default {
  components: { AppWrapperView },
  props: {},

  computed: {
    // 更新間隔の選択肢。言語切替に追従させるため computed で組み立てる
    intervalItems() {
      return [1, 2, 5, 10].map(n => ({ label: this.$t('fittingRoomPingLog.label.sec', { n }), value: n }));
    },

    lastUpdatedLabel() {
      if (!this.lastUpdated) return "-";
      return this.lastUpdated.toLocaleTimeString("ja-JP", { hour12: false });
    },
  },

  data: () => ({
    // 自動更新(既定ON・1秒)。アンテナを動かしながら数値の変化を見る用途のため既定でON。
    autoRefresh: true,
    intervalSec: 1,
    timerId: null,

    dataList: [],
    totalRows: 0,
    maxRows: null,
    lastUpdated: null,
    errorMessage: "",
    isSearching: false,

    searchCondition: {
      antennaNo: "",
      epc: "",
      judgedOnly: false,
      limit: 200,
    },
  }),

  methods: {
    search() {
      // 自動更新中に前回の応答が返っていない場合は間引く(1秒間隔で叩くため)
      if (this.isSearching) return;

      this.isSearching = true;
      api.post("/fitting-room-history/ping-log", {
        antennaNo: this.searchCondition.antennaNo?.trim() ?? "",
        epc: this.searchCondition.epc?.trim() ?? "",
        judgedOnly: this.searchCondition.judgedOnly,
        limit: this.searchCondition.limit,
      }).then((response) => {
        if (response.status == 1) {
          this.errorMessage = this.$t('fittingRoomPingLog.label.fetchError');
          return;
        }
        const results = response.data.results;
        this.dataList = (results?.pingLogList ?? []).map(this.decorate);
        this.totalRows = results?.totalRows ?? 0;
        this.maxRows = results?.maxRows ?? 0;
        this.lastUpdated = new Date();
        this.errorMessage = "";
      }).catch(() => {
        // 自動更新中は一時的な失敗で止めない。次の周回で回復する
        this.errorMessage = this.$t('fittingRoomPingLog.label.commError');
      }).finally(() => {
        this.isSearching = false;
      });
    },

    // APIは DateUtil により "yyyyMMddHHmmss"(14桁)で返す。
    // Date.parse はこの形式を解釈できないため自前で分解する。
    parseCompact(s) {
      if (!s || s.length < 14) return null;
      const d = new Date(
        Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8)),
        Number(s.slice(8, 10)), Number(s.slice(10, 12)), Number(s.slice(12, 14)));
      return isNaN(d.getTime()) ? null : d;
    },

    // 表示用に "MM-DD HH:mm:ss" へ整形する
    formatCompact(s) {
      if (!s || s.length < 14) return s ?? "";
      return s.slice(4, 6) + "-" + s.slice(6, 8) + " " +
             s.slice(8, 10) + ":" + s.slice(10, 12) + ":" + s.slice(12, 14);
    },

    // R420の時計とAWS到達時刻のずれを見せる。
    // 大きくずれていればR420側のNTP未同期を疑う(→ _etc/r420_デバッグ情報.md)
    decorate(row) {
      const ping = this.parseCompact(row.pingDatetime);
      const recv = this.parseCompact(row.receivedDatetime);
      let gapLabel = "-";
      if (ping && recv) {
        const sec = (ping.getTime() - recv.getTime()) / 1000;
        gapLabel = (sec >= 0 ? "+" : "") + sec.toFixed(0) + "s";
      }
      return Object.assign({}, row, {
        gapLabel,
        pingLabel: this.formatCompact(row.pingDatetime),
        recvLabel: this.formatCompact(row.receivedDatetime),
      });
    },

    // 弱い反応・未登録アンテナを目視で拾えるよう色を付ける
    rowClass(row) {
      if (!row.fittingRoomNo) return "row-unmapped";
      if (!row.judged) return "row-weak";
      return "";
    },

    onToggleAutoRefresh(value) {
      if (value) {
        this.search();
        this.restartTimer();
      } else {
        this.clearTimer();
      }
    },

    restartTimer() {
      this.clearTimer();
      if (!this.autoRefresh) return;
      this.timerId = setInterval(this.search, this.intervalSec * 1000);
    },

    clearTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    },
  },

  mounted() {
    this.search();
    this.restartTimer();
  },

  // 画面を離れてもタイマーが生き残ると1秒ごとにAPIを叩き続けるため必ず止める
  beforeUnmount() {
    this.clearTimer();
  },
};
</script>

<style scoped>
.text-mono {
  font-family: "Consolas", "Courier New", monospace;
}
/* 閾値に届かなかった反応 */
.row-weak {
  background-color: #fafafa;
  color: #9e9e9e;
}
/* m_fitting_room に未登録のリーダー/アンテナ */
.row-unmapped {
  background-color: #fff3e0;
}
</style>
